import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, TextField, Button, Paper, Card, CardContent, Grid, Tabs, Tab, IconButton, Tooltip, MenuItem } from '@mui/material';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';

export default function PioWidget() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [widgetConfig, setWidgetConfig] = useState({
    apiKey: 'your-api-key',
    model: 'your-model-id',
    theme: 'light',
    title: 'Your Widget',
    primaryColor: theme.palette.primary.main,
    bubbleColor: theme.palette.background.paper,
    greeting: 'Hallo! Wie kann ich Ihnen weiterhelfen?',
    customIcon: '',
    quickQuestions: [],
    enableNavigation: true
  });
  const [quickQuestionInput, setQuickQuestionInput] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const addQuickQuestion = () => {
    if (quickQuestionInput.trim()) {
      setWidgetConfig({
        ...widgetConfig,
        quickQuestions: [...widgetConfig.quickQuestions, quickQuestionInput.trim()]
      });
      setQuickQuestionInput('');
    }
  };

  const removeQuickQuestion = (index) => {
    setWidgetConfig({
      ...widgetConfig,
      quickQuestions: widgetConfig.quickQuestions.filter((_, i) => i !== index)
    });
  };

  const generateEmbedCode = () => {
    const escapeAttr = (value = '') => value.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const quickQuestions = JSON.stringify(widgetConfig.quickQuestions || []);
    const iconAttr = widgetConfig.customIcon ? ` data-icon="${escapeAttr(widgetConfig.customIcon)}"` : '';
    const quickQuestionsAttr = widgetConfig.quickQuestions.length
      ? ` data-quick-questions='${quickQuestions}'`
      : '';
    return `<script src="https://chat.piosphere.ai/widget.js"
  data-api-key="${escapeAttr(widgetConfig.apiKey)}"
  data-title="${escapeAttr(widgetConfig.title)}"
  data-greeting="${escapeAttr(widgetConfig.greeting)}"
  data-color="${widgetConfig.primaryColor}"
  data-bubble-color="${widgetConfig.bubbleColor}"
  data-theme="${widgetConfig.theme}"
  data-enable-navigation="${widgetConfig.enableNavigation}"
  data-model="${widgetConfig.model}"${quickQuestionsAttr}${iconAttr}>
</script>`;
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WidgetsIcon /> PioWidget
      </Typography>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab icon={<SettingsIcon />} label="Configuration" />
        <Tab icon={<PreviewIcon />} label="Embed" />
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Widget Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="API Key *"
                    value={widgetConfig.apiKey}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, apiKey: e.target.value })}
                    fullWidth
                    size="small"
                    required
                    helperText="Required for widget to function"
                  />
                  <TextField
                    label="Model"
                    value={widgetConfig.model}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, model: e.target.value })}
                    fullWidth
                    size="small"
                    placeholder="zai-org/GLM-4.5-Air"
                    helperText="Enter any model identifier"
                  />
                  <TextField
                    select
                    label="Theme"
                    value={widgetConfig.theme}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, theme: e.target.value })}
                    fullWidth
                    size="small"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Enable Navigation"
                    value={String(widgetConfig.enableNavigation)}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, enableNavigation: e.target.value === 'true' })}
                    fullWidth
                    size="small"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                  <TextField
                    label="Widget Title"
                    value={widgetConfig.title}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, title: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Primary Color"
                    type="color"
                    value={widgetConfig.primaryColor}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, primaryColor: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Bubble Color"
                    type="color"
                    value={widgetConfig.bubbleColor}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, bubbleColor: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Greeting Message"
                    value={widgetConfig.greeting}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, greeting: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                  <TextField
                    label="Custom Icon (SVG or URL)"
                    value={widgetConfig.customIcon}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, customIcon: e.target.value })}
                    fullWidth
                    size="small"
                    placeholder="Leave empty for default icon"
                    helperText="SVG code or image URL"
                  />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Quick Questions
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Add a quick question..."
                        value={quickQuestionInput}
                        onChange={(e) => setQuickQuestionInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addQuickQuestion();
                          }
                        }}
                      />
                      <Button variant="outlined" onClick={addQuickQuestion} size="small">
                        Add
                      </Button>
                    </Box>
                    {widgetConfig.quickQuestions.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {widgetConfig.quickQuestions.map((q, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Paper sx={{ p: 1, flex: 1, bgcolor: 'background.default' }}>
                              <Typography variant="body2">{q}</Typography>
                            </Paper>
                            <IconButton size="small" onClick={() => removeQuickQuestion(index)}>
                              ×
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Embed Code
                  </Typography>
                  <Tooltip title={copyFeedback ? "Copied!" : "Copy code"}>
                    <IconButton onClick={handleCopyCode} color="primary">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    position: 'relative'
                  }}
                >
                  <Box
                    component="pre"
                    sx={{
                      margin: 0,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      color: 'text.primary',
                      maxHeight: 400
                    }}
                  >
                    {generateEmbedCode()}
                  </Box>
                </Paper>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Copy and paste this code into your website's HTML, preferably before the closing &lt;/body&gt; tag.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Integration Guide
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      1. Configure Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Set your API key and customize the widget appearance in the Configuration tab.
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      2. Copy the Embed Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click the copy button above to copy the embed code to your clipboard.
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      3. Paste into your HTML
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Paste the code before the closing &lt;/body&gt; tag of your website.
                    </Typography>
                  </Paper>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Features
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    AI-powered chat interface with streaming responses
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Customizable theme, colors, and greeting message
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Quick questions for common inquiries
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Mobile responsive design
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Session management and conversation history
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Knowledge base integration with search
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
